import React from "react";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { Counter } from "@/components/common/Counter";
import { CTASection } from "@/components/home/CTASection";
import { Target, Eye, Handshake, Globe, CheckCircle } from "@phosphor-icons/react";
import { useT } from "@/context/LanguageContext";

const COVERAGE = ["United States", "United Kingdom", "UAE", "Nigeria", "Ghana", "Kenya", "South Africa", "India", "Europe", "Australia"];

export default function About() {
  const { t } = useT();
  const VALUES = [
    { icon: Target, title: t("about.missionTitle"), desc: t("about.missionDesc") },
    { icon: Eye, title: t("about.visionTitle"), desc: t("about.visionDesc") },
    { icon: Handshake, title: t("about.promiseTitle"), desc: t("about.promiseDesc") },
  ];
  return (
    <div data-testid="about-page">
      <PageHero
        overline={t("about.heroOverline")}
        title={t("about.heroTitle")}
        subtitle={t("about.heroSubtitle")}
        crumbs={[{ label: t("about.heroOverline") }]}
        image="https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg"
      />

      <section className="bg-white py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <img src="https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg" alt="Our team" className="h-[460px] w-full rounded-2xl object-cover" />
          </Reveal>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-teal">{t("about.whoOverline")}</p>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              {t("about.whoTitle")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              {t("about.whoP1")}
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              {t("about.whoP2")}
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              <div><p className="font-heading text-3xl font-bold text-ink"><Counter to={12} suffix="+" /></p><p className="text-xs text-slate-500">{t("about.statYears")}</p></div>
              <div><p className="font-heading text-3xl font-bold text-ink"><Counter to={3} /></p><p className="text-xs text-slate-500">{t("about.statOffices")}</p></div>
              <div><p className="font-heading text-3xl font-bold text-ink"><Counter to={96} suffix="%" /></p><p className="text-xs text-slate-500">{t("about.statRetention")}</p></div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-mist py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="h-full rounded-xl border border-slate-200 bg-white p-8">
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white">
                    <v.icon size={24} weight="duotone" />
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-semibold text-ink">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex items-center gap-3">
            <Globe size={28} weight="duotone" className="text-teal" />
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-ink sm:text-3xl">{t("about.coverageTitle")}</h2>
          </div>
          <p className="mt-3 max-w-2xl text-slate-600">{t("about.coverageSubtitle")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {COVERAGE.map((c) => (
              <span key={c} className="flex items-center gap-2 rounded-full border border-slate-200 bg-mist px-4 py-2 text-sm font-medium text-ink">
                <CheckCircle size={16} weight="fill" className="text-teal" /> {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
